import { useState, useEffect } from "react";
import type { FormEvent } from "react";
import { Mic } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useCreateItem, useProcessDump } from "@/hooks/useItems";
import { useCreateProject } from "@/hooks/useProjects";
import { useAuth } from "@/contexts/AuthContext";
import { useCommandStyle } from "@/contexts/CommandStyleContext";
import { useVoiceInput } from "@/hooks/useVoiceInput";
import { supabase } from "@/lib/supabase";
import { useQueryClient } from "@tanstack/react-query";
import { formatError } from "@/lib/utils";

// Simple heuristic: if it starts with a question word, contains "?",
// or is clearly conversational, route to chat instead of creating a task
function looksLikeQuestion(text: string): boolean {
  const t = text.trim().toLowerCase();
  if (t.includes("?")) return true;
  const questionStarts = [
    "did ", "do ", "does ", "is ", "are ", "was ", "were ", "has ", "have ",
    "can ", "could ", "would ", "should ", "will ", "what ", "where ", "when ",
    "who ", "why ", "how ", "which ", "check ", "show me ", "tell me ",
    "any ", "have i ", "did i ", "did my ", "is there ", "are there ",
  ];
  return questionStarts.some((q) => t.startsWith(q));
}

export function CaptureBar() {
  const [text, setText] = useState("");
  const [chatting, setChatting] = useState(false);
  const createItem = useCreateItem();
  const processDump = useProcessDump();
  const createProject = useCreateProject();
  const { user } = useAuth();
  const { copy } = useCommandStyle();
  const queryClient = useQueryClient();
  const { transcript, isListening, startListening, stopListening, resetTranscript, isSupported } = useVoiceInput();

  useEffect(() => {
    if (transcript) {
      setText(transcript);
    }
  }, [transcript]);

  const handleMicClick = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const handleChatResponse = async (rawInput: string) => {
    if (!user) return;
    setChatting(true);
    try {
      const { data, error } = await supabase.functions.invoke("chat", {
        body: {
          messages: [{ role: "user", content: rawInput }],
          user_id: user.id,
        },
      });
      if (error) throw error;
      const response = data?.response;
      if (response) {
        toast(response, { duration: 8000 });
      }
      // Refresh items in case chat created/modified any
      queryClient.invalidateQueries({ queryKey: ["items"] });
      queryClient.invalidateQueries({ queryKey: ["item-stats"] });
    } catch (err) {
      toast.error(`Chat error: ${formatError(err)}`);
    } finally {
      setChatting(false);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim() || createItem.isPending || chatting) return;

    const rawInput = text.trim();
    setText("");
    resetTranscript();

    // Route questions to chat AI
    if (looksLikeQuestion(rawInput)) {
      toast("Thinking...", { id: "capture-thinking", duration: 15000 });
      handleChatResponse(rawInput).then(() => {
        toast.dismiss("capture-thinking");
      });
      return;
    }

    // Otherwise capture as a task
    createItem.mutate(
      { title: rawInput, raw_input: rawInput, source: "manual" },
      {
        onSuccess: (item) => {
          if (user) {
            processDump.mutate(
              {
                item_id: item.id,
                raw_input: rawInput,
                user_id: user.id,
              },
              {
                onSuccess: (data) => {
                  if (data?.suggestions && Array.isArray(data.suggestions)) {
                    for (const suggestion of data.suggestions) {
                      toast(`AI suggests new project: ${suggestion.name}`, {
                        action: {
                          label: "Create",
                          onClick: () => {
                            const slug = suggestion.name
                              .toLowerCase()
                              .replace(/[^a-z0-9]+/g, "-")
                              .replace(/(^-|-$)/g, "");
                            createProject.mutate(
                              { name: suggestion.name, slug, color: "#64748B" },
                              {
                                onSuccess: () => toast.success(`Project "${suggestion.name}" created`),
                                onError: (err) => toast.error(formatError(err)),
                              }
                            );
                          },
                        },
                        duration: 10000,
                      });
                    }
                  }
                },
              }
            );
          }
          toast("Captured");
        },
        onError: (err) => {
          toast.error(
            err instanceof Error ? err.message : "Failed to capture item"
          );
        },
      }
    );
  };

  return (
    <div className="sticky top-0 z-10 bg-card border-b border-border px-4 py-3">
      <form onSubmit={handleSubmit} className="flex items-center gap-2">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={copy.capturePlaceholder}
          className="flex-1"
          disabled={createItem.isPending || chatting}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleMicClick}
          disabled={!isSupported}
          aria-label={isListening ? "Stop voice capture" : "Start voice capture"}
          aria-pressed={isListening}
          className={isListening ? "text-accent" : "text-muted-foreground"}
        >
          <Mic className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
}
